import axios from "axios";

// fundswalla.in/Api has no Access-Control-Allow-Origin — use same-origin proxy in local.
const taskApiUrl = (endpoint) =>
  process.env.NODE_ENV === "development"
    ? `/Api/TaskApi/${endpoint}`
    : `https://fundswalla.in/Api/TaskApi/${endpoint}`;

const CHECK_CUSTOMER_BY_PAN_URL = taskApiUrl("checkGetCustomerbyPan");
const CHECK_BLACKLIST_BY_PAN_URL = taskApiUrl("checkGetCustomerbyPanBlacklist");

const REAPPLY_ALLOWED_STATUSES = new Set([
  "REJECT",
  "REJECTED",
  "CLOSE",
  "CLOSED",
]);

const FRESH_STATUSES = new Set(["", "FRESH", "NEW"]);

const normalizeLoanStatus = (loanStatus) =>
  String(loanStatus || "").trim().toUpperCase();

const getStatusDecision = (loanStatus) => {
  const cleanStatus = normalizeLoanStatus(loanStatus);

  if (FRESH_STATUSES.has(cleanStatus)) {
    return {
      blocked: false,
      isFresh: true,
      loanStatus: cleanStatus || "FRESH",
      message: "PAN Verification Complete!",
    };
  }

  if (REAPPLY_ALLOWED_STATUSES.has(cleanStatus)) {
    return {
      blocked: false,
      isFresh: false,
      loanStatus: cleanStatus,
      message: `Latest Status: ${cleanStatus} (You can re-apply)`,
    };
  }

  return {
    blocked: true,
    isFresh: false,
    loanStatus: cleanStatus,
    message: `Your loan status is ${cleanStatus}. Cannot re-apply.`,
  };
};

/**
 * Blacklist + loan-status bucket check for PAN (fundswalla.in TaskApi).
 * Allowed only for: Fresh / REJECT / CLOSE(D).
 */
export const checkCustomerByPan = async (pan) => {
  const pancard = String(pan || "").trim().toUpperCase();

  if (pancard.length !== 10) {
    return { blocked: false, isFresh: true };
  }

  try {
    const [customerRes, blacklistRes] = await Promise.all([
      axios
        .get(CHECK_CUSTOMER_BY_PAN_URL, {
          params: { pancard },
          timeout: 20000,
          validateStatus: () => true,
        })
        .catch((err) => ({ failed: true, error: err })),
      axios
        .get(CHECK_BLACKLIST_BY_PAN_URL, {
          params: { pancard },
          timeout: 20000,
          validateStatus: () => true,
        })
        .catch((err) => ({ failed: true, error: err })),
    ]);

    const blacklistData = blacklistRes?.failed ? null : blacklistRes?.data;
    if (blacklistData?.blacklisted === true) {
      return {
        blocked: true,
        blacklisted: true,
        message:
          blacklistData.message ||
          "This PAN is blacklisted. Cannot apply.",
        customerData: null,
      };
    }

    const customerPayload = customerRes?.failed ? null : customerRes?.data;
    const httpStatus = customerRes?.status;

    // Fresh lead: not found / no data
    if (
      !customerPayload ||
      customerPayload.status === false ||
      httpStatus === 404 ||
      !customerPayload.data
    ) {
      return {
        blocked: false,
        isFresh: true,
        loanStatus: "FRESH",
        message: "PAN Verification Complete!",
        customerData: null,
      };
    }

    const loanStatus = normalizeLoanStatus(customerPayload.data.status);
    const decision = getStatusDecision(loanStatus);

    return {
      ...decision,
      customerData: customerPayload.data,
    };
  } catch (err) {
    // Do not hard-block apply if status APIs are down; verifyPan still runs.
    return {
      blocked: false,
      isFresh: true,
      checkFailed: true,
      message: "PAN Verification Complete!",
      error: err,
    };
  }
};
